import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const IntegrationsSection = ({ 
  formData, 
  handleInputChange, 
  handleCheckboxChange,
  errors 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [subscribeUrl, setSubscribeUrl] = useState('https://winglet.app/calendar/subscribe/abc123');

  const calendarOptions = [
    { value: 'google', label: 'Google Calendar' },
    { value: 'apple', label: 'Apple iCal' },
    { value: 'outlook', label: 'Outlook' }
  ];

  const notificationFrequencyOptions = [
    { value: 'realtime', label: 'Real-time (immediate)' },
    { value: 'daily', label: 'Daily digest (8 AM)' },
    { value: 'weekly', label: 'Weekly summary (Sunday night)' }
  ];

  const handleCopyUrl = () => {
    navigator?.clipboard?.writeText(subscribeUrl);
  };

  return (
    <div className="card">
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-xl bg-accent-blue/20 glow-subtle">
            <Icon name="Plug" size={20} color="var(--color-accent-blue)" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground">Integrations & Data Sync</h2>
            <p className="text-xs md:text-sm text-muted-foreground font-caption">
              Connect external services and calendars
            </p>
          </div>
        </div>
        <Icon 
          name={isExpanded ? "ChevronUp" : "ChevronDown"} 
          size={24} 
          color="var(--color-muted-foreground)" 
        />
      </div>

      {isExpanded && (
        <div className="mt-6 md:mt-8 space-y-6 md:space-y-8">
          <div>
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">Calendar Integration</h3>
            <Select
              label="Calendar Provider"
              options={calendarOptions}
              value={formData?.calendarProvider}
              onChange={(value) => handleInputChange({ target: { name: 'calendarProvider', value } })}
              placeholder="Select calendar provider"
              className="w-full mb-4"
            />

            {formData?.calendarProvider && (
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-tertiary border border-accent-blue/20">
                  <label className="text-sm font-medium text-foreground mb-2 block">Subscribe URL</label>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value={subscribeUrl}
                      readOnly
                      className="flex-1 font-mono text-xs"
                    />
                    <Button
                      variant="outline"
                      iconName="Copy"
                      onClick={handleCopyUrl}
                    >
                      Copy
                    </Button>
                  </div>
                </div>

                <div className="space-y-3">
                  <Checkbox
                    label="Include commute flights"
                    checked={formData?.calendarIncludeCommute}
                    onChange={(e) => handleCheckboxChange('calendarIncludeCommute', e?.target?.checked)}
                  />
                  <Checkbox
                    label="Include rest days"
                    checked={formData?.calendarIncludeRest}
                    onChange={(e) => handleCheckboxChange('calendarIncludeRest', e?.target?.checked)}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="h-px bg-accent-blue/20"></div>

          <div>
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">FlightAware Integration</h3>
            <Input
              label="FlightAware API Key (Optional)"
              type="password"
              name="flightAwareApiKey"
              value={formData?.flightAwareApiKey}
              onChange={handleInputChange}
              placeholder="Enter API key for unlimited tracking"
              description="Optional: For real-time flight tracking. Get key at flightaware.com/commercial"
              className="w-full"
            />
          </div>

          <div className="h-px bg-accent-blue/20"></div>

          <div>
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">Email Notifications</h3>
            <Input
              label="Email Address"
              type="email"
              name="notificationEmail"
              value={formData?.notificationEmail}
              onChange={handleInputChange}
              placeholder="pilot@example.com"
              error={errors?.notificationEmail}
              className="w-full mb-4"
            />

            <Select
              label="Notification Frequency"
              options={notificationFrequencyOptions}
              value={formData?.notificationFrequency}
              onChange={(value) => handleInputChange({ target: { name: 'notificationFrequency', value } })}
              placeholder="Select frequency"
              className="w-full mb-4"
            />

            <div className="space-y-3">
              <Checkbox
                label="Schedule changes"
                checked={formData?.notifyScheduleChanges}
                onChange={(e) => handleCheckboxChange('notifyScheduleChanges', e?.target?.checked)}
              />
              <Checkbox
                label="Delay alerts (>30 min)"
                checked={formData?.notifyDelays}
                onChange={(e) => handleCheckboxChange('notifyDelays', e?.target?.checked)}
              />
              <Checkbox
                label="Gate changes"
                checked={formData?.notifyGateChanges}
                onChange={(e) => handleCheckboxChange('notifyGateChanges', e?.target?.checked)}
              />
              <Checkbox
                label="Compliance warnings"
                checked={formData?.notifyCompliance}
                onChange={(e) => handleCheckboxChange('notifyCompliance', e?.target?.checked)}
              />
              <Checkbox
                label="Pay discrepancies detected"
                checked={formData?.notifyPayDiscrepancies}
                onChange={(e) => handleCheckboxChange('notifyPayDiscrepancies', e?.target?.checked)}
              />
            </div>
          </div>

          <div className="h-px bg-accent-blue/20"></div>

          <div>
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">Push Notifications</h3>
            <Checkbox
              label="Enable push notifications"
              description="Receive mobile notifications for important events"
              checked={formData?.enablePushNotifications}
              onChange={(e) => handleCheckboxChange('enablePushNotifications', e?.target?.checked)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationsSection;